import EventBus from '../services/service.eventBus';
import { deleteImage, fetchImages, getPresignedURL, uploadImage, validateImage } from '../services/service.image-capture';
import { state as imageCaptureState } from '../store/store.image-capture';
import { ImageCaptureEvents, trackImageCapture } from '../tracking/tracking.imageCapture';
import { EMPTY_URLS, ImageCategory, getCategoryIndex, getCategoryIndexFromValue, getCategoryKeyFromValue } from '../utils/utils.image-capture';

const onReceivedParams = ({ imageGroupGuid, requiresAllPhotos, shouldShowError }) => {
  try {
    onReceivedParamsSuccess({ imageGroupGuid, requiresAllPhotos, shouldShowError });
  } catch (error) {
    onReceivedParamsFail(error);
  }
};

const onReceivedParamsSuccess = ({ imageGroupGuid, requiresAllPhotos, shouldShowError }) => {
  EventBus.publish('ReceivedParams', { imageGroupGuid, requiresAllPhotos, shouldShowError });
  imageCaptureState.imageGroupGuid = imageGroupGuid;
  imageCaptureState.requiresAllPhotos = requiresAllPhotos;
  imageCaptureState.shouldShowError = shouldShowError;
  EventBus.publish('ReceivedParamsSuccess', {});
};

const onReceivedParamsFail = (error) => {
  EventBus.publish('ReceivedParamsFail', error);
};

const onLoadImages = async (imageGroupGuid: string) => {
  try {
    await onLoadImagesSuccess(imageGroupGuid);
  } catch (error) {
    onLoadImagesFail(error);
  }
};

const onLoadImagesSuccess = async (imageGroupGuid: string) => {
  EventBus.publish('LoadImages', imageGroupGuid);

  const images = (await fetchImages(imageGroupGuid)) || [];

  const newImageUrls = [...EMPTY_URLS];
  images.map((image) => {
    const categoryIndex = getCategoryIndex(image.category);
    newImageUrls[categoryIndex] = image.thumbnailUrl || image.imageUrl;
  });

  imageCaptureState.imageUrls = newImageUrls;

  EventBus.publish('LoadImagesSuccess', { imageUrls: imageCaptureState.imageUrls });
};

const onLoadImagesFail = (error) => {
  EventBus.publish('LoadImagesFail', error);
  imageCaptureState.imageUrls = [...EMPTY_URLS];
};

const onUpload = async (imageFile: File, imageGroupGuid: string, category: ImageCategory) => {
  try {
    const isValid = await onUploadSuccess(imageFile, imageGroupGuid, category);
    return isValid;
  } catch (error) {
    onUploadFail(error, category);
    return false;
  }
};

const onUploadSuccess = async (imageFile: File, imageGroupGuid: string, category: ImageCategory) => {
  EventBus.publish('Upload', { imageFile, imageGroupGuid, category });

  const presigned = await getPresignedURL(imageFile.name);
  await uploadImage(presigned.uploadUrl, imageFile);
  const isValid = await validateImage(presigned.fileName, imageGroupGuid, getCategoryKeyFromValue(category));
  if (isValid) {
    imageCaptureState.imageUrls[getCategoryIndexFromValue(category)] = presigned.uploadUrl;
    imageCaptureState.imageUrls = [...imageCaptureState.imageUrls];
  }

  EventBus.publish('UploadSuccess', { isValid, imageUrls: imageCaptureState.imageUrls });
  trackImageCapture(ImageCaptureEvents.UploadSuccess, 'impression', category);
  return isValid;
};

const onUploadFail = (error, category) => {
  EventBus.publish('UploadFail', error);
  trackImageCapture(ImageCaptureEvents.UploadFail, 'impression', category);
};

const onDelete = async (imageGroupGuid: string, category: ImageCategory) => {
  trackImageCapture(ImageCaptureEvents.Delete, 'click');
  try {
    await onDeleteSuccess(imageGroupGuid, category);
    return true;
  } catch (error) {
    onDeleteFail(error);
    return false;
  }
};

const onDeleteSuccess = async (imageGroupGuid: string, category: ImageCategory) => {
  EventBus.publish('Delete', { imageGroupGuid, category });

  await deleteImage(imageGroupGuid, getCategoryKeyFromValue(category));

  imageCaptureState.imageUrls[getCategoryIndexFromValue(category)] = '';
  imageCaptureState.imageUrls = [...imageCaptureState.imageUrls];

  EventBus.publish('DeleteSuccess', { imageUrls: imageCaptureState.imageUrls });
};
const onDeleteFail = (error) => {
  EventBus.publish('DeleteFail', error);
};

const onStoreUpdate = (props = {}) => {
  Object.entries(props).forEach(([name, value]) => {
    imageCaptureState[name] = value;
  });
};

export default {
  onLoadImages,
  onReceivedParams,
  onUpload,
  onDelete,
  onStoreUpdate,
};
